import {ILocation} from '../models/cleanUp';

/**
 * Converts decimal degrees to degrees minutes seconds.
 *
 * @param dd the decimal degrees value.
 * @param isLng specifies whether the decimal degrees value is a longitude.
 * @return degrees minutes seconds string in the format 49°15'51.35"N
 */
export const convertToDms = (dd: number, isLng?: boolean) => {
    const dir = dd < 0 ? (isLng ? 'W' : 'S') : isLng ? 'E' : 'N';

    const absDd = Math.abs(dd);
    const deg = absDd | 0;
    const frac = absDd - deg;
    const min = (frac * 60) | 0;
    let sec = frac * 3600 - min * 60;
    // Round it to 2 decimal points.
    sec = Math.round(sec * 100) / 100;
    return deg + '°' + min + "'" + sec + '"' + dir;
};

export const coordinatesToString = (coordinates?: ILocation) => {
    return coordinates
        ? `${convertToDms(+coordinates.longitude, true)}, ${convertToDms(
              +coordinates.latitude,
              false
          )}`
        : '-';
};

// Parses a single coordinate component string into decimal degrees.
// Accepts: 49°15'51.35"N  |  49 15 51.35 N  |  -49.263709  |  49.263709N
const parseDmsComponent = (str: string): number | null => {
    str = str.trim();
    if (/^-?\d+(\.\d+)?$/.test(str)) return parseFloat(str);

    const dirMatch = str.match(/[NSEWnsew]/);
    const dir = dirMatch?.[0].toUpperCase();
    const nums = str
        .replace(/[°'"NSEWnsew]/g, ' ')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map(Number);

    if (nums.length < 1 || nums.some(isNaN)) return null;

    const [deg = 0, min = 0, sec = 0] = nums;
    let dd = deg + min / 60 + sec / 3600;
    if (dir === 'S' || dir === 'W') dd = -dd;
    return dd;
};

/**
 * Parses a latitude or longitude string, accepting either decimal degrees
 * or DMS format (e.g. 37°58'38"N), and range-validates the result.
 *
 * @return the decimal degrees value, or null if unparseable or out of range.
 */
export const parseCoordinateComponent = (
    str: string,
    kind: 'lat' | 'lng'
): number | null => {
    const value = parseDmsComponent(str);
    if (value === null) return null;
    if (kind === 'lat' && (value < -90 || value > 90)) return null;
    if (kind === 'lng' && (value < -180 || value > 180)) return null;
    return value;
};
